import type { ReactFlowInstance } from 'reactflow';
import type { CustomNode, CustomEdge, CustomNodeData, CustomGenericNodeData } from '@src/components/custom-nodes/types';
import {
  getNearestNodeByType,
  getNodesBetween,
} from '@src/scenario-editor/render-utils';

interface ReorderStatementBranchParams {
  instance: ReactFlowInstance;
  node: CustomNode;
  branchIndex: number;
  newPositionIndex: number;
}

export const reorderStatementBranch = ({
  instance,
  node,
  branchIndex,
  newPositionIndex
}: ReorderStatementBranchParams) => {
  const nodes = instance.getNodes() as CustomNode[];
  const edges = instance.getEdges() as CustomEdge[];
  const nodeBranchIndexPosition = node.branchId.split('-').length;

  const startNode = node;
  const endNode = getNearestNodeByType(nodes, edges, node, 'IfCloseNode');

  if (!endNode) {
    return;
  }

  // Get all nodes between start and end
  const nodesBetween = getNodesBetween(nodes, edges, startNode, endNode);

  // Group nodes by their branch index
  const nodesGroupedByBranch = nodesBetween.reduce((acc: Record<string, CustomNode[]>, node) => {
    const branchIndex = node.branchId.split('-')[nodeBranchIndexPosition];
    acc[branchIndex] = acc[branchIndex] ?? [];
    acc[branchIndex].push(node);
    return acc;
  }, {});

  // Get the branch we want to move
  const branchToMove = nodesGroupedByBranch[branchIndex + 1];
  if (!branchToMove) {
    return;
  }

  // Remove the branch from its current position
  delete nodesGroupedByBranch[branchIndex + 1];

  // Create new ordered branches array
  const branches = Object.entries(nodesGroupedByBranch);
  branches.splice(newPositionIndex, 0, [String(branchIndex + 1), branchToMove]);
  const parentBranchId = node.branchId;
  const branchesWithFixedBranchIds = branches.map(([branchIndex, nodes], idx) => {
    const updatedNodes = nodes.map((node: CustomNode) => {
      const branchId = node.branchId.replace(`${parentBranchId}-${branchIndex}`, `${parentBranchId}-${idx + 1}`);
      const nodeData = node.data as CustomGenericNodeData;
      if (node.type === 'IfBranchNode') {
        return {
          ...node,
          branchId,
          parentBranchId: branchId.split('-').slice(0, -1).join('-'),
          data: {
            ...node.data,
            message: (String(nodeData?.message) || '').replace(`${branchIndex}`, `${idx + 1}`),
            label: (String(nodeData?.label) || '')?.replace(`${branchIndex}`, `${idx + 1}`),
          } as CustomNodeData,
        };
      }
      return {
        ...node,
        branchId,
        parentBranchId: branchId.split('-').slice(0, -1).join('-'),
      };
    });
    return [branchIndex, updatedNodes] as const;
  });
  const normalizedByNodeId = {} as Record<string, CustomNode>;
  branchesWithFixedBranchIds.forEach(([, nodes]) => {
    nodes.forEach(node => {
      normalizedByNodeId[node.id] = node;
    });
  });

  const updatedNodes = nodes.map(node => normalizedByNodeId[node.id] || node);

  return {
    updatedNodes,
    edges,
  };
}
